import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { Button } from "@components/controls/Button"
import { ZodForm } from "@components/forms/lib/ZodForm"
import { fieldTypes } from "@components/forms/lib/form-support"
import { useNewsletterSubscribe, useRegisterCustomer } from "@hooks/queries"
import { CaretLeftIcon } from "@radix-ui/react-icons"
import { useEffect, useMemo } from "react"
import { z } from "zod"
import { useAuth } from "../hooks/useAuth"
import { Field } from "@components/controls/Field"
import { TextInput } from "@components/controls/TextInput"

const baseSchema = z.object({
  name: fieldTypes.fullName.describe("Full Name"),
  branch: fieldTypes.branch,
  pronouns: fieldTypes.pronouns,
  dateOfBirth: fieldTypes.date.optional().describe("Date of Birth"),
  email: fieldTypes.confirmEmail.describe("Your Email"),
  phone: z
    .string()
    .regex(/[0-9]{3}/, "Please enter your mobile number")
    .min(7, "Please enter your mobile number")
    .describe("Mobile Phone"),
  password: fieldTypes.confirmPassword.describe("Choose a Password"),
  // password: z.object({
  //   password: fieldTypes.newPassword.describe("Password"),
  //   fieldTypes.password.describe("Confirm Password")
  // })
  nonMember: z.boolean().optional(),
  terms: z.boolean().optional(),
  newsletter: z.boolean().optional(),
})

type FormData = z.infer<typeof baseSchema>

type Props = {
  onComplete: () => void
  onGoBack: () => void
  forceEmail?: string
  disableSignup?: boolean
  agreements?: {
    nonMember?: string
    terms?: string
    newsletter?: string
  }
}

export function RegisterAccount(props: Props) {
  const auth = useAuth()
  const createAccount = useRegisterCustomer()

  const schema = useMemo(() => {
    return baseSchema.extend({
      nonMember: props.agreements?.nonMember ? z.boolean().refine((v) => v, { message: "You must agree to these terms to continue" }) : z.boolean().optional(),
      terms: props.agreements?.terms ? z.boolean().refine((v) => v, { message: "You must agree to these terms to continue" }) : z.boolean().optional(),
      newsletter: z.boolean().optional(),
    })
  }, [])

  useEffect(() => {
    document.scrollingElement?.scrollTo({ top: 0, behavior: "smooth" })
  }, [createAccount.isError, createAccount.isPaused])

  useEffect(() => {
    if (createAccount.isSuccess && createAccount.data?.registerCustomer?.authToken!) {
      props.onComplete()
      auth.handleLoggedIn({
        authToken: createAccount.data?.registerCustomer?.authToken!,
        name: createAccount.data?.registerCustomer?.customer?.firstName!,
        refreshToken: createAccount.data?.registerCustomer?.refreshToken!,
        email: createAccount.data?.registerCustomer?.customer?.email!,
      })
    } else if (auth.isLoggedIn()) {
      props.onComplete()
    }
  }, [createAccount.isSuccess, auth.isLoggedIn()])

  return (
    <div className="grid-auto">
      <div className="col-span-12 md:col-span-6 lg:col-span-4">
        {createAccount.isError && (
          <ErrorMessage className="mb-5">
            <span dangerouslySetInnerHTML={{ __html: createAccount.error.message }} />
          </ErrorMessage>
        )}
        <ZodForm
          schema={schema}
          loading={createAccount.isPending}
          onSubmit={(data) => {
            createAccount.reset()
            createAccount.mutate({
              input: {
                firstName: data.name.first,
                lastName: data.name.last,
                email: data.email.value,
                password: data.password.value,
                shipping: {
                  phone: data.phone,
                },
                billing: {
                  phone: data.phone,
                  email: data.email.value,
                },
                metaData: [
                  {
                    key: "branch",
                    value: data.branch,
                  },
                  {
                    key: "pronouns",
                    value: data.pronouns ?? "",
                  },
                  {
                    key: "birthday",
                    value: data.dateOfBirth ?? "",
                  },
                ],
              },
            })
            if (data.newsletter) {
              useNewsletterSubscribe.mutate({
                email: data.email.value,
              })
            }
          }}
          data={{
            email: props.forceEmail
              ? ({
                  value: props.forceEmail!,
                  confirm: props.forceEmail!,
                } as any)
              : null,
          }}
          mapping={[]}
          render={(fields, Layout) => (
            <>
              <Layout.Heading>Your details</Layout.Heading>
              <Layout.Stack>
                <Layout.Describe children={fields.name} />
                <Layout.Grid>
                  {fields.dateOfBirth}
                  {fields.pronouns}
                  {fields.phone}
                  {props.forceEmail ? (
                    <Field label="Email Address" note="You can change your email later in account settings">
                      <TextInput disabled value={props.forceEmail} />
                    </Field>
                  ) : null}
                </Layout.Grid>
              </Layout.Stack>

              <Layout.Spacer />

              <Layout.Heading>Login details</Layout.Heading>
              <Layout.Stack>
                {props.forceEmail ? null : fields.email}
                <Layout.Describe note="Password must be 8 characters or longer">{fields.password}</Layout.Describe>
              </Layout.Stack>

              <Layout.Spacer />

              <Layout.Heading>Select your nearest AGDA branch</Layout.Heading>
              <Layout.Grid>{fields.branch}</Layout.Grid>

              {props.agreements?.nonMember || props.agreements?.terms || props.agreements?.newsletter ? (
                <>
                  <Layout.Spacer />

                  <Layout.Stack>
                    {props.agreements?.nonMember && <Layout.Describe note={props.agreements.nonMember}>{fields.nonMember}</Layout.Describe>}
                    {props.agreements?.terms && <Layout.Describe note={props.agreements.terms}>{fields.terms}</Layout.Describe>}
                    {props.agreements?.newsletter && <Layout.Describe note={props.agreements.newsletter}>{fields.newsletter}</Layout.Describe>}
                  </Layout.Stack>
                </>
              ) : null}

              <Layout.SubmitBar submitLabel="Continue">
                <Button type="button" variant="outline" leftIcon={<CaretLeftIcon />} onClick={props.onGoBack}>
                  Go Back
                </Button>
              </Layout.SubmitBar>
            </>
          )}
        />
      </div>
    </div>
  )
}
